// ChatProvider.tsx
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import SendbirdChat from '@sendbird/chat';

import { chatTokenSelector, userIdSelector, userProfileSelector } from 'domain/env';
import { ghostIdentities } from 'domain/chat';
import { getRuntimeConfig } from 'lib/runtimeConfig';

import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';

import { useTheme, alpha } from '@mui/material';
import '@sendbird/uikit-react/dist/index.css';

interface Props {
  children: React.ReactNode;
}

// Shared across all ChatProvider instances — cancels any in-flight disconnect when the same
// user reopens chat before the previous disconnect completed (race: close → quick reopen).
// Storing userId ensures we never skip a disconnect or trigger a reconnect for a different user.
let pendingDisconnect: { cancel: () => void; userId: string } | null = null;

const ChatProvider: React.FC<Props> = ({ children }) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const userID = useSelector(userIdSelector);
  const userProfile = useSelector(userProfileSelector);
  const chatToken = useSelector(chatTokenSelector);

  const sendbirdAppId = getRuntimeConfig().env.REACT_APP_SENDBIRD_APP_ID;

  const stringSet = useMemo(() => ({
    CHANNEL_SETTING__HEADER__TITLE: formatMessage({ id: 'chat.settings.title', defaultMessage: 'Chat information' }),
    CHANNEL_SETTING__MEMBERS__OPERATOR: formatMessage({ id: 'chat.settings.ownerRole', defaultMessage: 'Owner' }),
    LABEL__OPERATOR: formatMessage({ id: 'chat.settings.ownerRole', defaultMessage: 'Owner' }),
    // Edit chat information modal — override Sendbird's "Channel" wording with "Chat".
    MODAL__CHANNEL_INFORMATION__TITLE: formatMessage({ id: 'chat.settings.editTitle', defaultMessage: 'Edit chat information' }),
    MODAL__CHANNEL_INFORMATION__CHANNEL_NAME: formatMessage({ id: 'chat.settings.chatName', defaultMessage: 'Chat name' }),
    MODAL__CHANNEL_INFORMATION__CHANNEL_IMAGE: formatMessage({ id: 'chat.settings.chatImage', defaultMessage: 'Chat image' }),

    // Remaining Sendbird strings that reference "Channel" — overridden to use DOKKA's "Chat" wording.
    // Group channel settings
    CHANNEL_PREVIEW_MOBILE_LEAVE: formatMessage({ id: 'chat.settings.leaveChat', defaultMessage: 'Leave chat' }),
    CHANNEL_SETTING__LEAVE_CHANNEL__TITLE: formatMessage({ id: 'chat.settings.leaveChat', defaultMessage: 'Leave chat' }),
    CHANNEL_SETTING__FREEZE_CHANNEL: formatMessage({ id: 'chat.settings.freezeChat', defaultMessage: 'Freeze chat' }),
    // Open channel list / settings
    OPEN_CHANNEL_LIST__TITLE: formatMessage({ id: 'chat.openChannel.listTitle', defaultMessage: 'Chats' }),
    CREATE_OPEN_CHANNEL_LIST__TITLE: formatMessage({ id: 'chat.openChannel.newProfile', defaultMessage: 'New chat profile' }),
    CREATE_OPEN_CHANNEL_LIST__SUBTITLE__IMG_SECTION: formatMessage({ id: 'chat.settings.chatImage', defaultMessage: 'Chat image' }),
    CREATE_OPEN_CHANNEL_LIST__SUBTITLE__TEXT_SECTION: formatMessage({ id: 'chat.settings.chatName', defaultMessage: 'Chat name' }),
    CREATE_OPEN_CHANNEL_LIST__SUBTITLE__TEXT_PLACE_HOLDER: formatMessage({ id: 'chat.openChannel.enterName', defaultMessage: 'Enter chat name' }),
    OPEN_CHANNEL_SETTINGS__OPERATOR_TITLE: formatMessage({ id: 'chat.settings.title', defaultMessage: 'Chat information' }),
    OPEN_CHANNEL_SETTINGS__DELETE_CHANNEL_PANEL: formatMessage({ id: 'chat.openChannel.deleteChat', defaultMessage: 'Delete chat' }),
    OPEN_CHANNEL_SETTINGS__DELETE_CHANNEL_TITLE: formatMessage({ id: 'chat.openChannel.deleteChatConfirm', defaultMessage: 'Delete chat?' }),
    OPEN_CHANNEL_SETTINGS__DELETE_CHANNEL_CONTEXT: formatMessage({ id: 'chat.openChannel.deleteChatContext', defaultMessage: "Once deleted, this chat can't be restored." }),
    // Common
    CHANNEL_FROZEN: formatMessage({ id: 'chat.common.frozen', defaultMessage: 'Chat is frozen' }),
    PLACE_HOLDER__NO_CHANNEL: formatMessage({ id: 'chat.common.noChats', defaultMessage: 'No chats' }),
    // Modals
    MODAL__CHOOSE_CHANNEL_TYPE__TITLE: formatMessage({ id: 'chat.modal.newChat', defaultMessage: 'New chat' }),
    MODAL__CREATE_CHANNEL__TITLE: formatMessage({ id: 'chat.modal.newChat', defaultMessage: 'New chat' }),
    MODAL__LEAVE_CHANNEL__TITLE: formatMessage({ id: 'chat.modal.leaveChatConfirm', defaultMessage: 'Leave this chat?' }),
    MODAL__VOICE_MESSAGE_INPUT_DISABLED__TITLE_FROZEN: formatMessage({ id: 'chat.common.frozenPeriod', defaultMessage: 'Chat is frozen.' }),
    // Message input
    MESSAGE_INPUT__PLACE_HOLDER__DISABLED: formatMessage({ id: 'chat.input.unavailable', defaultMessage: 'Chat is unavailable in this chat' }),
    MESSAGE_INPUT__PLACE_HOLDER__FROZEN: formatMessage({ id: 'chat.input.unavailable', defaultMessage: 'Chat is unavailable in this chat' }),
    // Context menu / search
    MESSAGE_MENU__OPEN_IN_CHANNEL: formatMessage({ id: 'chat.menu.openInChat', defaultMessage: 'Open in chat' }),
    SEARCH_IN_CHANNEL: formatMessage({ id: 'chat.search.inChat', defaultMessage: 'Search in chat' }),

    // ---- Reachable UI strings (no "Channel" wording, but user-facing & translatable) ----
    // Message input
    MESSAGE_INPUT__PLACE_HOLDER: formatMessage({ id: 'chat.input.placeholder', defaultMessage: 'Enter message' }),
    MESSAGE_INPUT__QUOTE_REPLY__PLACE_HOLDER: formatMessage({ id: 'chat.input.replyPlaceholder', defaultMessage: 'Reply to message' }),
    MESSAGE_INPUT__PLACE_HOLDER__MUTED: formatMessage({ id: 'chat.input.muted', defaultMessage: "Chat is unavailable because you're muted" }),
    MESSAGE_INPUT__PLACE_HOLDER__MUTED_SHORT: formatMessage({ id: 'chat.input.mutedShort', defaultMessage: "You're muted" }),

    // Message menu (Reply / Copy / Edit / Delete / Resend / Thread / Mark as unread)
    MESSAGE_MENU__COPY: formatMessage({ id: 'chat.menu.copy', defaultMessage: 'Copy' }),
    MESSAGE_MENU__REPLY: formatMessage({ id: 'chat.menu.reply', defaultMessage: 'Reply' }),
    MESSAGE_MENU__EDIT: formatMessage({ id: 'chat.menu.edit', defaultMessage: 'Edit' }),
    MESSAGE_MENU__DELETE: formatMessage({ id: 'chat.menu.delete', defaultMessage: 'Delete' }),
    MESSAGE_MENU__RESEND: formatMessage({ id: 'chat.menu.resend', defaultMessage: 'Resend' }),
    MESSAGE_MENU__THREAD: formatMessage({ id: 'chat.menu.replyInThread', defaultMessage: 'Reply in thread' }),
    MESSAGE_MENU__MARK_AS_UNREAD: formatMessage({ id: 'chat.menu.markAsUnread', defaultMessage: 'Mark as unread' }),

    // Message states
    MESSAGE_EDITED: formatMessage({ id: 'chat.message.edited', defaultMessage: '(edited)' }),
    MODAL__DELETE_MESSAGE__TITLE: formatMessage({ id: 'chat.modal.deleteMessage', defaultMessage: 'Delete this message?' }),

    // Connection / placeholders
    TRYING_TO_CONNECT: formatMessage({ id: 'chat.common.connecting', defaultMessage: 'Trying to connect…' }),
    PLACE_HOLDER__WRONG: formatMessage({ id: 'chat.common.wentWrong', defaultMessage: 'Something went wrong' }),
    PLACE_HOLDER__RETRY_TO_CONNECT: formatMessage({ id: 'chat.common.retry', defaultMessage: 'Retry' }),
    PLACE_HOLDER__NO_MESSAGES: formatMessage({ id: 'chat.panel.noMessages', defaultMessage: 'No messages' }),

    // Typing indicator
    TYPING_INDICATOR__IS_TYPING: formatMessage({ id: 'chat.typing.isTyping', defaultMessage: 'is typing…' }),
    TYPING_INDICATOR__AND: formatMessage({ id: 'chat.typing.and', defaultMessage: 'and' }),
    TYPING_INDICATOR__ARE_TYPING: formatMessage({ id: 'chat.typing.areTyping', defaultMessage: 'are typing…' }),
    TYPING_INDICATOR__MULTIPLE_TYPING: formatMessage({ id: 'chat.typing.multipleTyping', defaultMessage: 'Several people are typing…' }),

    // Thread
    THREAD__HEADER_TITLE: formatMessage({ id: 'chat.thread.title', defaultMessage: 'Thread' }),
    THREAD__INPUT__REPLY_TO_THREAD: formatMessage({ id: 'chat.thread.replyToThread', defaultMessage: 'Reply to thread' }),
    THREAD__INPUT__REPLY_IN_THREAD: formatMessage({ id: 'chat.thread.replyInThread', defaultMessage: 'Reply in thread' }),

    // Quote / reply
    QUOTE_MESSAGE_INPUT__REPLY_TO: formatMessage({ id: 'chat.quote.replyTo', defaultMessage: 'Reply to' }),
    QUOTED_MESSAGE__REPLIED_TO: formatMessage({ id: 'chat.quote.repliedTo', defaultMessage: 'replied to' }),
    QUOTED_MESSAGE__CURRENT_USER: formatMessage({ id: 'chat.quote.currentUser', defaultMessage: 'You' }),
    QUOTED_MESSAGE__UNAVAILABLE: formatMessage({ id: 'chat.quote.unavailable', defaultMessage: 'Message unavailable' }),

    // Common buttons — reuse the app-wide button.* keys where they exist (present in all
    // locales). BUTTON__INVITE has no button.* equivalent, so it uses the chat.menu.invite key.
    BUTTON__SUBMIT: formatMessage({ id: 'button.submit', defaultMessage: 'Submit' }),
    BUTTON__CANCEL: formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' }),
    BUTTON__DELETE: formatMessage({ id: 'button.delete', defaultMessage: 'Delete' }),
    BUTTON__SAVE: formatMessage({ id: 'button.save', defaultMessage: 'Save' }),
    BUTTON__CREATE: formatMessage({ id: 'button.create', defaultMessage: 'Create' }),
    BUTTON__INVITE: formatMessage({ id: 'chat.menu.invite', defaultMessage: 'Invite' }),
    BUTTON__OK: formatMessage({ id: 'button.ok', defaultMessage: 'OK' }),

    // Edit chat information modal — "Upload" picture button and the name-field placeholder,
    // plus the "Edit" action on the settings profile row. These are reached only through the
    // DOKKA EditChannelModal/ChannelSettings wrappers and had no "Channel" wording, so earlier
    // localization passes (which keyed off "Channel" or a feature-area list) missed them (DA-19471).
    CHANNEL_SETTING__PROFILE__EDIT: formatMessage({ id: 'chat.settings.edit', defaultMessage: 'Edit' }),
    MODAL__CHANNEL_INFORMATION__UPLOAD: formatMessage({ id: 'chat.settings.upload', defaultMessage: 'Upload' }),
    MODAL__CHANNEL_INFORMATION__INPUT__PLACE_HOLDER: formatMessage({ id: 'chat.settings.enterName', defaultMessage: 'Enter name' }),

    // Message list — the "N new/unread message(s) on {date}" jump-to-latest notification bar.
    CHANNEL__MESSAGE_LIST__NOTIFICATION__NEW_MESSAGE: formatMessage({ id: 'chat.notification.newMessage', defaultMessage: 'new message' }),
    CHANNEL__MESSAGE_LIST__NOTIFICATION__NEW_MESSAGE_S: formatMessage({ id: 'chat.notification.newMessages', defaultMessage: 'new messages' }),
    CHANNEL__MESSAGE_LIST__NOTIFICATION__ON: formatMessage({ id: 'chat.notification.on', defaultMessage: 'on' }),
    CHANNEL__MESSAGE_LIST__NOTIFICATION__UNREAD_MESSAGE: formatMessage({ id: 'chat.notification.unreadMessage', defaultMessage: 'unread message' }),
    CHANNEL__MESSAGE_LIST__NOTIFICATION__UNREAD_MESSAGE_S: formatMessage({ id: 'chat.notification.unreadMessages', defaultMessage: 'unread messages' }),

    // Member list — "(You)" suffix on the current user's row in channel settings.
    CHANNEL_SETTING__MEMBERS__YOU: formatMessage({ id: 'chat.settings.membersYou', defaultMessage: ' (You)' }),

    // Channel-list last-message preview — file-type labels shown in place of a text preview.
    CHANNEL_PREVIEW_LAST_MESSAGE_FILE_TYPE_AUDIO: formatMessage({ id: 'chat.preview.fileAudio', defaultMessage: 'Audio' }),
    CHANNEL_PREVIEW_LAST_MESSAGE_FILE_TYPE_VOICE: formatMessage({ id: 'chat.preview.fileVoice', defaultMessage: 'Voice message' }),
    CHANNEL_PREVIEW_LAST_MESSAGE_FILE_TYPE_GENERAL: formatMessage({ id: 'chat.preview.fileGeneral', defaultMessage: 'File' }),
    CHANNEL_PREVIEW_LAST_MESSAGE_FILE_TYPE_GIF: formatMessage({ id: 'chat.preview.fileGif', defaultMessage: 'GIF' }),
    CHANNEL_PREVIEW_LAST_MESSAGE_FILE_TYPE_PHOTO: formatMessage({ id: 'chat.preview.filePhoto', defaultMessage: 'Photo' }),
    CHANNEL_PREVIEW_LAST_MESSAGE_FILE_TYPE_VIDEO: formatMessage({ id: 'chat.preview.fileVideo', defaultMessage: 'Video' }),
    CHANNEL_PREVIEW_LAST_MESSAGE_TEMPLATE_MESSAGE: formatMessage({ id: 'chat.preview.templateMessage', defaultMessage: 'Message' }),

    // Name/title/member fallbacks shown when Sendbird has no value.
    NO_NAME: formatMessage({ id: 'chat.common.noName', defaultMessage: '(No name)' }),
    NO_TITLE: formatMessage({ id: 'chat.common.noTitle', defaultMessage: '(No title)' }),
    NO_MEMBERS: formatMessage({ id: 'chat.common.noMembers', defaultMessage: '(No members)' }),

    // Message timestamp relative label.
    MESSAGE_STATUS__YESTERDAY: formatMessage({ id: 'chat.message.yesterday', defaultMessage: 'Yesterday' }),

    // Read-receipt / member tooltips.
    TOOLTIP__YOU: formatMessage({ id: 'chat.tooltip.you', defaultMessage: 'you' }),
    TOOLTIP__AND_YOU: formatMessage({ id: 'chat.tooltip.andYou', defaultMessage: ', and you' }),
    TOOLTIP__UNKNOWN_USER: formatMessage({ id: 'chat.tooltip.unknownUser', defaultMessage: '(no name)' }),

    // Unreadable / unsupported message fallbacks.
    UNKNOWN__CANNOT_READ_MESSAGE: formatMessage({ id: 'chat.unknown.cannotRead', defaultMessage: 'Cannot read this message.' }),
    UNKNOWN__UNKNOWN_MESSAGE_TYPE: formatMessage({ id: 'chat.unknown.messageType', defaultMessage: '(Unknown message type)' }),
  }), [formatMessage]);

  // SendbirdProvider is per-user, not per-company — mount it whenever the user is
  // authenticated and not a ghost. Company-level feature gating lives in ChatLauncher.
  const isAvailable = sendbirdAppId && chatToken && userID && !ghostIdentities.includes(userID);

  useEffect(() => {
    // Only manage the Sendbird connection when the SDK is actually going to be used.
    // If isAvailable is false, SendbirdProvider never mounts so there is nothing to disconnect.
    if (!isAvailable) return;

    // Cancel a pending disconnect only if it belongs to the same user — a different user's
    // disconnect must complete so their session is fully closed before the new one starts.
    if (pendingDisconnect?.userId === userID) {
      pendingDisconnect.cancel();
    }
    pendingDisconnect = null;

    return () => {
      const disconnectingUserId = userID;
      let cancelled = false;
      pendingDisconnect = {
        cancel: () => { cancelled = true; },
        userId: disconnectingUserId,
      };
      SendbirdChat.instance?.disconnect().then(() => {
        if (cancelled) {
          // The same user reopened chat before disconnect completed — restore the connection.
          SendbirdChat.instance?.reconnect();
        }
      }).catch(() => {});
    };
    // isAvailable is stable by the time ChatDrawer mounts (rendered only post-auth).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Map MUI theme tokens to Sendbird CSS variables via the colorSet prop.
  // This is the ONLY supported way to override Sendbird's colors — GlobalStyles
  // on :root does NOT work because Sendbird's internal useTheme() calls
  // css-vars-ponyfill which overwrites :root variables AFTER GlobalStyles renders.
  // The colorSet prop is merged into Sendbird's DEFAULT_COLOR_SET before applying,
  // so our values win.
  const colorSet = useMemo(
    () => ({
      // Font family
      '--sendbird-font-family-default': theme.typography.fontFamily as string,

      // Regular font weight for chat menu-item labels — a DOKKA-custom var (Sendbird
      // has no font-weight token). Theme-backed via theme.typography.fontWeightRegular
      // (dokkaTheme has no typography override, so this is MUI's default 400). Consumed
      // by the menu-item weight override in styles.css. String() cast: fontWeightRegular
      // is typed number | string, and CSS custom properties must be strings.
      '--dokka-chat-font-weight-regular': String(theme.typography.fontWeightRegular),

      // Primary palette
      '--sendbird-light-primary-500': theme.palette.primary.dark,
      '--sendbird-light-primary-400': theme.palette.primary.dark,
      '--sendbird-light-primary-300': theme.palette.primary.main,
      '--sendbird-light-primary-200': alpha(theme.palette.primary.main, 0.5),
      '--sendbird-light-primary-100': alpha(theme.palette.primary.main, 0.12),

      // Secondary palette
      '--sendbird-light-secondary-500': theme.palette.secondary.dark,
      '--sendbird-light-secondary-400': theme.palette.secondary.dark,
      '--sendbird-light-secondary-300': theme.palette.action.selected,
      '--sendbird-light-secondary-200': alpha(theme.palette.secondary.light, 0.5),
      '--sendbird-light-secondary-100': alpha(theme.palette.secondary.light, 0.2),

      // Information palette → mapped to primary (not info)
      '--sendbird-light-information-500': theme.palette.primary.dark,
      '--sendbird-light-information-400': theme.palette.primary.dark,
      '--sendbird-light-information-300': theme.palette.primary.main,
      '--sendbird-light-information-200': alpha(theme.palette.primary.main, 0.5),
      '--sendbird-light-information-100': alpha(theme.palette.primary.main, 0.12),

      // Background palette
      '--sendbird-light-background-100': theme.palette.action.hover,
      '--sendbird-light-background-200': theme.palette.grey[300],
      '--sendbird-light-background-300': theme.palette.grey[400],

      // Chat conversation surface (off-white #FBFDFF) — a DOKKA-custom var (NOT a
      // Sendbird token; Sendbird has no off-white background token). Consumed by
      // `.sendbird-conversation` in styles.css. Kept in sync with the MUI theme via
      // theme.palette.chat.surface so EmptyPanelContent and Sendbird match. Falls back
      // to the literal for themes that don't define palette.chat (e.g. the bootstrap
      // theme in initializers/src/theme) so this never crashes.
      '--dokka-chat-surface': theme.palette.chat?.surface ?? 'rgba(251, 253, 255, 1)',

      // Text palette (on light backgrounds)
      '--sendbird-light-onlight-01': theme.palette.text.primary,
      '--sendbird-light-onlight-02': theme.palette.text.secondary,
      '--sendbird-light-onlight-03': theme.palette.text.disabled,
      '--sendbird-light-onlight-04': theme.palette.divider,

      // Text palette (on dark backgrounds)
      '--sendbird-light-ondark-01': theme.palette.primary.contrastText,
      '--sendbird-light-ondark-02': theme.palette.primary.contrastText,

      // Error palette
      '--sendbird-light-error-500': theme.palette.error.dark,
      '--sendbird-light-error-400': theme.palette.error.dark,
      '--sendbird-light-error-300': theme.palette.error.main,
    }),
    [theme],
  );

  if (!isAvailable) return <>{children}</>;

  return (
    <SendbirdProvider
      appId={sendbirdAppId}
      userId={userProfile.id}
      accessToken={chatToken}
      theme={theme.palette.mode}
      htmlTextDirection={theme.direction}
      stringSet={stringSet}
      colorSet={colorSet}
    >
      {children}
    </SendbirdProvider>
  );
};

export default ChatProvider;
